import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useDemoMask } from 'hooks/useDemoMask';
import { buildBreadcrumbsFromRoute, buildMenuBreadcrumbs } from 'utils/buildBreadcrumbsFromRoute';
import { BREADCRUMB_PARENTS_STATE_KEY } from 'utils/breadcrumbNavigation';
import { maskBreadcrumbLabels } from 'utils/demoMask';

const HIDE_BREADCRUMB_PATHS = [
  '/user/invite-new-user/invite',
  /^\/user\/invite-new-user\/[^/]+\/invite$/,
];

function shouldHideBreadcrumbs(pathname) {
  if (!pathname) return false;
  return HIDE_BREADCRUMB_PATHS.some((rule) =>
    typeof rule === 'string' ? rule === pathname : rule.test(pathname),
  );
}

function readName(value) {
  if (value == null || value === '') return undefined;
  return String(value);
}

/**
 * Resolves breadcrumb items from the current URL, optional location.state parents,
 * and loaded entity labels in Redux.
 */
export function useBreadcrumbTrail(menuItems) {
  const { isDemo } = useDemoMask();
  const location = useLocation();
  const router = useSelector((state) => state.getIn(['router']));
  const pathname = router?.location?.pathname || location.pathname || '';

  const buildingDetail = useSelector((state) => state.getIn(['property', 'building-detail']));
  const applicationDetail = useSelector((state) => state.getIn(['property', 'application-detail']));
  const policyDetail = useSelector((state) => state.getIn(['property', 'policy-detail']));
  const claimDetail = useSelector((state) => state.getIn(['property', 'claim-detail']));
  const landlordDetail = useSelector((state) => state.getIn(['user', 'landlord_detail']));
  const invoiceDetail = useSelector((state) => state.getIn(['invoice', 'invoiceDetailData']));
  const details = useSelector((state) => state.getIn(['ui', 'details']));

  const labels = useMemo(() => {
    const buildingJSON = buildingDetail?.toJS?.() || null;
    const applicationJSON = applicationDetail?.toJS?.() || null;
    const policyJSON = policyDetail?.toJS?.() || null;
    const claimJSON = claimDetail?.toJS?.() || null;
    const landlordJSON = landlordDetail?.toJS?.() || null;
    const invoiceJSON = invoiceDetail?.toJS?.() || null;
    const detailsJSON = details?.toJS?.() || null;

    const fromDetails = (() => {
      if (!detailsJSON) return {};
      switch (detailsJSON.type) {
        case 'property':
          return { buildingName: readName(detailsJSON.data?.name) };
        case 'application':
          return { applicationName: readName(detailsJSON.data?.name) };
        case 'policy':
        case 'flagged-cancellation':
          return { policyName: readName(detailsJSON.data?.name) };
        case 'claim':
          return { claimName: readName(detailsJSON.data?.claim_name) };
        case 'invoice':
          return {
            invoiceLabel: readName(
              detailsJSON.data?.doc_number ? `${detailsJSON.data.doc_number} - Invoice` : undefined,
            ),
          };
        case 'landlord':
          return { landlordName: readName(detailsJSON.name || detailsJSON.data?.name) };
        case 'user':
          return {
            userName: readName(detailsJSON.accepter?.username || detailsJSON.data?.username),
          };
        case 'email':
        case 'message':
          return {
            emailLabel: readName(
              detailsJSON.requestor?.username ||
                detailsJSON.data?.requestor?.username ||
                detailsJSON.data?.requestor_email,
            ),
            notificationLabel: readName(
              detailsJSON.data?.requestor?.username ||
                detailsJSON.data?.requestor_email ||
                'Notification',
            ),
          };
        default:
          return {};
      }
    })();

    const rawLabels = {
      buildingName: readName(buildingJSON?.data?.name) || fromDetails.buildingName,
      applicationName: readName(applicationJSON?.data?.name) || fromDetails.applicationName,
      policyName: readName(policyJSON?.data?.name) || fromDetails.policyName,
      flaggedCancellationName: readName(policyJSON?.data?.name) || fromDetails.policyName,
      claimName: readName(claimJSON?.data?.claim_name) || fromDetails.claimName,
      landlordName:
        readName(landlordJSON?.name || landlordJSON?.company_name) || fromDetails.landlordName,
      invoiceLabel:
        readName(
          invoiceJSON?.data?.doc_number ? `${invoiceJSON.data.doc_number} - Invoice` : undefined,
        ) || fromDetails.invoiceLabel,
      userName: fromDetails.userName,
      emailLabel: fromDetails.emailLabel,
      notificationLabel: fromDetails.notificationLabel,
    };

    return maskBreadcrumbLabels(rawLabels, isDemo);
  }, [
    buildingDetail,
    applicationDetail,
    policyDetail,
    claimDetail,
    landlordDetail,
    invoiceDetail,
    details,
    isDemo,
  ]);

  const routeItems = useMemo(
    () => buildBreadcrumbsFromRoute(pathname, location.state, labels),
    [pathname, location.state?.[BREADCRUMB_PARENTS_STATE_KEY], labels],
  );

  const menuCrumbs = useMemo(() => buildMenuBreadcrumbs(menuItems), [menuItems]);

  const items = routeItems.length > 0 ? routeItems : menuCrumbs;
  const hidden = shouldHideBreadcrumbs(pathname);
  const currentItem = items.length ? items[items.length - 1].name : null;

  return {
    items: hidden ? [] : items,
    currentItem,
    hidden,
  };
}
